import React, { FC, FormEvent, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'
import AuthLayout from './AuthLayout'
import arrowIcon from './assets/arrow.svg'
import { Path } from 'core/Routes'
import { khilanetLogin, KhilanetAuthError } from './khilanetApi'
import { setPending } from './khilanetAuth'

const PRIMARY = '#434FA2'
const TITLE = '#434FA2'
const HELP_TEXT = '#7a838f'
const INPUT_BG = '#e0e1e1'
const ERROR_RED = '#dc2626'
const LINK_BLUE = '#2563eb'

// Shape of validation errors the parent component renders. Keep a single union so the
// disabled-vs-error vs ok states stay obvious at the call site.
type LoginError = 'format' | 'not-registered' | 'network' | null

const Title = styled.h1`
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 27px;
  line-height: 36px;
  letter-spacing: 0.3955px;
  color: ${TITLE};
  text-align: center;
  margin: 0;
  max-width: 520px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 538px;
  margin-top: 22px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 48px;
  background: ${INPUT_BG};
  border: 0.909px solid ${p => p.hasError ? ERROR_RED : PRIMARY};
  border-radius: 6px;
  padding: 4px 12px;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: #1f2937;
  text-align: right;
  direction: rtl;
  outline: none;

  &::placeholder {
    color: ${HELP_TEXT};
  }

  &:focus {
    border-color: ${PRIMARY};
    box-shadow: 0 0 0 3px ${PRIMARY}1a;
  }
`

const Helper = styled.p<{ tone?: 'normal' | 'error' }>`
  margin: 0;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
  text-align: right;
  width: 100%;
  color: ${p => p.tone === 'error' ? ERROR_RED : HELP_TEXT};
`

const SubmitGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 59px;
  width: 100%;
  margin-top: 12px;
`

const SubmitButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
  color: white;
  transition: background 150ms ease;

  ${p => p.disabled ? css`
    background: rgba(67, 79, 162, 0.32);
    cursor: not-allowed;
  ` : css`
    background: ${PRIMARY};
    &:hover { background: #3b4595; }
  `}
`

// Submit-arrow: figma uses a -135deg rotation on the asset (a small tilted triangle that
// reads as a leftward/back arrow in RTL).
const Arrow = styled.img`
  width: 12.36px;
  height: 12.36px;
  transform: rotate(-135deg);
`

const Terms = styled.p`
  margin: 0;
  font-family: liebling, system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.4px;
  color: ${HELP_TEXT};
  text-align: center;

  a {
    color: ${LINK_BLUE};
    text-decoration: underline;
    &:hover { border-bottom: none; }
  }
`

const PLACEHOLDER = 'מספר סלולרי או כתובת אימייל'
const HELP_DEFAULT = 'לאחר שליחת הפרטים, יישלח אליך קוד אימות חד-פעמי להתחברות מאובטחת למערכת.'
const HELP_FORMAT_ERROR = 'מספר הסלולרי או כתובת האימייל שהוזנו אינם תקינים, נסו שוב.'
const HELP_NOT_REGISTERED = 'המספר או כתובת האימייל לא רשומים במערכת. פנו לשירות הלקוחות.'
const HELP_NETWORK = 'אירעה תקלה בחיבור לשרת. נסו שוב בעוד רגע.'

const isLikelyEmail = (s: string) => /^\S+@\S+\.\S+$/.test(s)
const isLikelyPhone = (s: string) => /^[0-9+\-\s]{8,}$/.test(s)

const LoginKhilanet: FC = () => {
  const h = useHistory()
  const [value, setValue] = useState('')
  const [error, setError] = useState<LoginError>(null)
  const [submitting, setSubmitting] = useState(false)

  const canSubmit = value.trim().length > 0 && !submitting

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const v = value.trim()
    if (!v || submitting) return

    if (!isLikelyEmail(v) && !isLikelyPhone(v)) {
      setError('format')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      // khilanetLogin sends the OTP and returns the short-lived 2FA token; the OTP screen picks
      // it up from the pending bridge. (In non-prod builds the dummy user short-circuits this.)
      const { twoFAToken, otpType } = await khilanetLogin(v)
      setPending({ phone: v, twoFAToken, otpType, requestedAt: Date.now() })
      h.push(Path.KHILANET_OTP)
    } catch (err) {
      if (err instanceof KhilanetAuthError && err.kind === 'network') {
        setError('network')
      } else {
        // The upstream's rejections at this leg are effectively "username not recognized".
        setError('not-registered')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    if (error) setError(null)
  }

  const helperText = error === 'format'
    ? HELP_FORMAT_ERROR
    : error === 'not-registered'
      ? HELP_NOT_REGISTERED
      : error === 'network'
        ? HELP_NETWORK
        : HELP_DEFAULT

  return (
    <AuthLayout>
      <Title>{`לקוחות 'מקומי' ו'קהילה-נט', ברוכים הבאים לאתר ההטבות של 'מקומי-נט'`}</Title>
      <Form onSubmit={handleSubmit} noValidate>
        <FieldGroup>
          <Input
            autoFocus
            value={value}
            onChange={handleChange}
            placeholder={PLACEHOLDER}
            type="text"
            autoComplete="username"
            inputMode="text"
            hasError={!!error}
            aria-invalid={!!error}
            aria-describedby="login-helper"
          />
          <Helper id="login-helper" tone={error ? 'error' : 'normal'}>
            {helperText}
          </Helper>
        </FieldGroup>
        <SubmitGroup>
          <SubmitButton type="submit" disabled={!canSubmit}>
            <Arrow src={arrowIcon} alt="" />
            שליחה
          </SubmitButton>
          <Terms>
            בכניסה לאתר אני מאשר/ת את{' '}
            <a href="#">תנאי השימוש</a>
            {' ו'}
            <a href="#">מדיניות הפרטיות</a>
          </Terms>
        </SubmitGroup>
      </Form>
    </AuthLayout>
  )
}

export default LoginKhilanet
